import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSecurityQuestion, resetPassword } from '../services/api';
import './ForgotPassword.css';

function ForgotPassword() {
  const navigate = useNavigate();

  // Step 1: username entry
  // Step 2: security question answer
  // Step 3: new password entry
  // Step 4: success
  const [step, setStep] = useState(1);

  const [username, setUsername] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Step 1: Look up security question ──────────────────────────────
  const handleFetchQuestion = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('Please enter your username');
      return;
    }

    setLoading(true);
    try {
      const data = await getSecurityQuestion(username.trim());
      if (data.success) {
        setSecurityQuestion(data.question);
        setStep(2);
      } else {
        setError(data.message || 'Username not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Username not found or no security question set');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify answer and move to password reset ───────────────
  const handleVerifyAnswer = (e) => {
    e.preventDefault();
    setError('');

    if (!securityAnswer.trim()) {
      setError('Please enter your answer');
      return;
    }

    // We don't pre-verify here — the backend checks on final submit
    // This prevents brute-force probing of the answer
    setStep(3);
  };

  // ── Step 3: Submit new password ─────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const data = await resetPassword(username, securityAnswer, newPassword, confirmPassword);
      if (data.success) {
        setStep(4);
      } else {
        setError(data.message || 'Password reset failed');
        // If answer was wrong, send back to step 2
        if (data.message?.toLowerCase().includes('answer')) {
          setSecurityAnswer('');
          setStep(2);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Password reset failed';
      setError(msg);
      if (msg.toLowerCase().includes('answer')) {
        setSecurityAnswer('');
        setStep(2);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Step indicator ──────────────────────────────────────────────────
  const stepLabels = ['Enter Username', 'Security Question', 'New Password', 'Done'];

  return (
    <div className="forgot-container">
      <div className="forgot-card">

        {/* Header */}
        <div className="forgot-header">
          <h1>🔑 Reset Password</h1>
          <p>Follow the steps to regain access to your account</p>
        </div>

        {/* Step progress bar */}
        {step < 4 && (
          <div className="step-indicator">
            {stepLabels.slice(0, 3).map((label, i) => (
              <React.Fragment key={i}>
                <div className={`step-dot ${step > i ? 'done' : step === i + 1 ? 'active' : ''}`}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                {i < 2 && <div className={`step-line ${step > i + 1 ? 'done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="forgot-error">
            ⚠️ {error}
          </div>
        )}

        {/* ── STEP 1: Username ── */}
        {step === 1 && (
          <form onSubmit={handleFetchQuestion} className="forgot-form">
            <p className="step-description">Enter the username for your account.</p>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setError(''); }}
                placeholder="Enter your username"
                disabled={loading}
                autoFocus
              />
            </div>
            <button type="submit" className="forgot-btn" disabled={loading}>
              {loading ? 'Looking up…' : 'Continue →'}
            </button>
            <button type="button" className="back-link" onClick={() => navigate('/login')}>
              ← Back to Login
            </button>
          </form>
        )}

        {/* ── STEP 2: Security Question ── */}
        {step === 2 && (
          <form onSubmit={handleVerifyAnswer} className="forgot-form">
            <p className="step-description">Answer your security question to verify your identity.</p>
            <div className="security-question-box">
              <span className="sq-label">Your question:</span>
              <p className="sq-text">"{securityQuestion}"</p>
            </div>
            <div className="form-group">
              <label htmlFor="securityAnswer">Your Answer</label>
              <input
                id="securityAnswer"
                type="text"
                value={securityAnswer}
                onChange={(e) => { setSecurityAnswer(e.target.value); setError(''); }}
                placeholder="Enter your answer"
                disabled={loading}
                autoFocus
              />
              <small className="hint">Answers are not case-sensitive</small>
            </div>
            <button type="submit" className="forgot-btn" disabled={loading || !securityAnswer.trim()}>
              Continue →
            </button>
            <button type="button" className="back-link" onClick={() => { setStep(1); setError(''); }}>
              ← Back
            </button>
          </form>
        )}

        {/* ── STEP 3: New Password ── */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="forgot-form">
            <p className="step-description">Choose a new password. You cannot reuse any previous password.</p>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                placeholder="At least 6 characters"
                disabled={loading}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Re-enter new password"
                disabled={loading}
              />
            </div>
            {/* Password match indicator */}
            {confirmPassword && (
              <p className={`match-indicator ${newPassword === confirmPassword ? 'match' : 'no-match'}`}>
                {newPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </p>
            )}
            <button
              type="submit"
              className="forgot-btn"
              disabled={loading || !newPassword || newPassword !== confirmPassword}
            >
              {loading ? 'Resetting…' : '🔑 Reset Password'}
            </button>
            <button type="button" className="back-link" onClick={() => { setStep(2); setError(''); }}>
              ← Back
            </button>
          </form>
        )}

        {/* ── STEP 4: Success ── */}
        {step === 4 && (
          <div className="forgot-success">
            <div className="success-icon">🎉</div>
            <h2>Password Reset!</h2>
            <p>Your password has been updated successfully. You can now log in with your new password.</p>
            <button className="forgot-btn" onClick={() => navigate('/login')}>
              Go to Login →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

export default ForgotPassword;